import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { MarketCard } from "@/components/Social";
import { BrowseMarket, MyShelf, Offers } from "@/pages/Social";
import { Sparkles, Store, Inbox, UserRound, Ruler, Users, ArrowRight, Compass } from "lucide-react";
import { toast } from "sonner";

const Row = ({ title, sub, items, testid, empty }) => (
  <section data-testid={testid}>
    <div className="flex items-end justify-between gap-3 mb-3">
      <div><p className="eyebrow">{title}</p>{sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}</div>
    </div>
    {items.length === 0 ? <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">{empty}</div>
      : <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">{items.map((it) => <MarketCard key={it.id} item={it} onOffered={() => {}} />)}</div>}
  </section>
);

/** Curated market: ranked for this user (fit, their brands/colours, sellers they follow or like, photo quality, freshness). */
const ForYou = () => {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("all");
  const [fit, setFit] = useState(false);
  useEffect(() => {
    setData(null);
    const params = {};
    if (kind !== "all") params.kind = kind;
    if (fit) params.fit = true;
    api.marketForYou(params).then(setData).catch(() => { toast.error("Could not load your market"); setData({ items: [], from_following: [], fresh_fits: [], taste: {} }); });
  }, [kind, fit]);

  return (
    <div className="space-y-8" data-testid="market-foryou">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full bg-secondary p-1 text-sm">
          {[["all", "Everything"], ["sell", "For sale"], ["swap", "Swaps"]].map(([k, l]) => <button key={k} onClick={() => setKind(k)} className={`px-3 py-1 rounded-full ${kind === k ? "bg-foreground text-background" : ""}`} data-testid={`foryou-kind-${k}`}>{l}</button>)}
        </div>
        <label className="inline-flex items-center gap-2 rounded-full border border-border px-3 h-9 text-sm cursor-pointer" data-testid="foryou-fit-toggle"><Ruler className="h-4 w-4" /> Only my size <Switch checked={fit} onCheckedChange={setFit} /></label>
        {data?.taste && (data.taste.brands?.length > 0 || data.taste.colors?.length > 0) && (
          <p className="text-xs text-muted-foreground ml-auto hidden md:block" data-testid="foryou-taste">Curated from your {data.taste.brands?.length || 0} brands, {data.taste.colors?.length || 0} go-to colours{data.taste.following ? ` and ${data.taste.following} sellers you follow` : ""}.</p>
        )}
      </div>

      {data === null ? <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}</div> : (
        <>
          <Row title="Picked for you" sub="Fits you, brands you own, your colours, sellers you follow - ranked." items={data.items.slice(0, 15)} testid="foryou-picked"
            empty="Nothing listed by others yet. As people list pieces, the ones that fit you and match your closet land here first." />
          {data.taste?.following > 0 ? (
            <Row title="From sellers you follow" items={data.from_following} testid="foryou-following" empty="The sellers you follow haven't listed anything yet." />
          ) : (
            <div className="rounded-2xl border border-border bg-gray-25 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="foryou-follow-nudge">
              <div className="flex items-center gap-3"><span className="h-9 w-9 rounded-xl bg-foreground text-background grid place-items-center"><Users className="h-4 w-4" /></span><div><p className="text-sm font-medium">Follow a few sellers</p><p className="text-xs text-muted-foreground">Their shelves get a dedicated row here and rank higher everywhere.</p></div></div>
              <Button asChild variant="outline" size="sm" className="rounded-full"><Link to="/social?tab=people" data-testid="foryou-go-people">Find sellers <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            </div>
          )}
          <Row title="Fresh in your size" sub="Newest listings that fit you." items={data.fresh_fits.slice(0, 10)} testid="foryou-fresh" empty="Add sizes to a few pieces in your closet so we know what fits you." />
          {data.items.length > 15 && <Row title="More for you" items={data.items.slice(15, 40)} testid="foryou-more" empty="" />}
        </>
      )}
    </div>
  );
};

export default function MarketPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "foryou";
  const [counts, setCounts] = useState({ pending: 0, unread: 0, total: 0 });
  const refreshCounts = () => api.offersPending().then(setCounts).catch(() => {});
  useEffect(() => { refreshCounts(); }, [tab]);
  const badge = counts.total || 0;
  const setTab = (v) => { const n = new URLSearchParams(params); n.set("tab", v); setParams(n); };

  return (
    <div data-testid="market-page">
      <p className="eyebrow">Market</p>
      <h1 className="font-display text-[34px] sm:text-[44px] leading-[1.02] mt-1">Picked for your closet</h1>
      <p className="text-muted-foreground mt-1.5">A market curated from what you own, wear and follow. Buy, swap or negotiate - and run your own shelf.</p>
      <Tabs value={tab} onValueChange={setTab} className="mt-5">
        <TabsList className="rounded-full">
          <TabsTrigger value="foryou" className="rounded-full" data-testid="market-tab-foryou"><Sparkles className="h-4 w-4 mr-1.5" /> For you</TabsTrigger>
          <TabsTrigger value="browse" className="rounded-full" data-testid="market-tab-browse"><Compass className="h-4 w-4 mr-1.5" /> Browse</TabsTrigger>
          <TabsTrigger value="mine" className="rounded-full" data-testid="market-tab-mine"><UserRound className="h-4 w-4 mr-1.5" /> My shelf</TabsTrigger>
          <TabsTrigger value="offers" className="rounded-full" data-testid="market-tab-offers"><Inbox className="h-4 w-4 mr-1.5" /> Offers{badge > 0 && <Badge className="ml-1.5 h-5 px-1.5 bg-wait text-wait-foreground hover:bg-wait" title={`${counts.pending} awaiting you · ${counts.unread} unread`} data-testid="market-offers-badge">{badge}</Badge>}</TabsTrigger>
        </TabsList>
        <TabsContent value="foryou" className="mt-5"><ForYou /></TabsContent>
        <TabsContent value="browse" className="mt-5"><BrowseMarket /></TabsContent>
        <TabsContent value="mine" className="mt-5"><MyShelf /></TabsContent>
        <TabsContent value="offers" className="mt-5"><Offers onChanged={refreshCounts} /></TabsContent>
      </Tabs>
    </div>
  );
}
